import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Plus, Package } from "lucide-react";
import { cn } from "@/lib/utils";

interface Specification {
  label: string;
  value: string;
}

interface ProductCardProps {
  id: string;
  title: string;
  description?: string | null;
  category?: string | null;
  image?: string | null;
  specifications?: Specification[] | Record<string, string> | null;
  shortlistButtonText?: string | null;
  productType?: string | null;
  technologyType?: string | null;
  onQuickView?: () => void;
  onAddToEnquiry?: () => void;
  isInEnquiry?: boolean;
}

const categoryColors: Record<string, { bg: string; text: string }> = {
  valves: { bg: "bg-slate-700", text: "text-white" },
  hydraulics: { bg: "bg-amber-700", text: "text-white" },
  electronics: { bg: "bg-emerald-700", text: "text-white" },
  robotics: { bg: "bg-blue-700", text: "text-white" },
  services: { bg: "bg-purple-700", text: "text-white" },
  sensors: { bg: "bg-cyan-700", text: "text-white" },
  motors: { bg: "bg-rose-700", text: "text-white" },
  controllers: { bg: "bg-indigo-700", text: "text-white" },
  actuators: { bg: "bg-teal-700", text: "text-white" },
  power: { bg: "bg-orange-700", text: "text-white" },
  connectors: { bg: "bg-violet-700", text: "text-white" },
  networking: { bg: "bg-sky-700", text: "text-white" },
  other: { bg: "bg-gray-700", text: "text-white" },
};

function getCategoryStyle(category: string | null | undefined) {
  if (!category) return categoryColors.other;
  const key = category.toLowerCase().replace(/[^a-z]/g, "");
  return categoryColors[key] || categoryColors.other;
}

function normalizeSpecs(specs: Specification[] | Record<string, string> | null | undefined): Specification[] {
  if (!specs) return [];
  if (Array.isArray(specs)) return specs;
  return Object.entries(specs).map(([label, value]) => ({ label, value }));
}

export function ProductCard({
  id,
  title,
  description,
  category,
  image,
  specifications,
  shortlistButtonText,
  productType,
  technologyType,
  onQuickView,
  onAddToEnquiry,
  isInEnquiry = false,
}: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const categoryStyle = getCategoryStyle(category);
  const specs = normalizeSpecs(specifications);
  
  const buttonText = shortlistButtonText || 
    (productType ? `Add ${productType} to Enquiry` : "Add to Enquiry");

  return (
    <Card
      className={cn(
        "group relative flex flex-col overflow-visible transition-all duration-300",
        "border-2",
        isHovered && "border-orange-400/60 shadow-lg shadow-orange-500/10"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${id}`}
    >
      <div className="relative h-48 overflow-hidden rounded-t-md bg-muted/30 isolate">
        {category && (
          <Badge 
            variant="secondary"
            className={cn(
              "absolute top-3 left-3 z-30 px-2.5 py-1 text-xs font-medium capitalize border-0",
              categoryStyle.bg,
              categoryStyle.text
            )}
            style={{ transform: "translateZ(1px)" }}
          >
            {category}
          </Badge>
        )}
        {image ? (
          <img
            src={image}
            alt={title}
            className={cn(
              "absolute inset-0 z-0 h-full w-full object-cover transition-transform duration-500",
              isHovered && "scale-110"
            )}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package className="h-16 w-16 text-muted-foreground/30" />
          </div>
        )}

        <div 
          className={cn(
            "absolute inset-0 z-10 flex items-center justify-center bg-black/0 transition-all duration-300",
            isHovered && "bg-black/20"
          )}
        >
          <Button
            variant="secondary"
            size="sm"
            className={cn(
              "gap-1.5 opacity-0 transition-opacity duration-300 shadow-lg",
              isHovered && "opacity-100"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onQuickView?.();
            }}
            data-testid={`button-quick-view-${id}`}
          >
            <Eye className="h-3.5 w-3.5" />
            Quick View
          </Button>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-2 text-base font-semibold leading-tight line-clamp-2">
          {title}
        </h3>

        {description && (
          <p className="mb-3 text-sm text-muted-foreground line-clamp-2">
            {description}
          </p>
        )}

        {specs.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {specs.slice(0, 4).map((spec, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-1 rounded bg-muted/60 px-2 py-1 text-xs"
              >
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {spec.label}:
                </span>
                <span className="text-foreground">{spec.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto">
          <Button
            variant={isInEnquiry ? "default" : "default"}
            className={cn(
              "w-full gap-1.5 font-medium",
              isInEnquiry 
                ? "bg-green-600 border-green-700 text-white"
                : "bg-orange-500 border-orange-600 text-white"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onAddToEnquiry?.();
            }}
            data-testid={`button-add-enquiry-${id}`}
          >
            <Plus className="h-4 w-4" />
            {isInEnquiry ? "Added to Enquiry" : buttonText}
          </Button>
        </div>
      </div>
    </Card>
  );
}
